/**
 * Business dashboard layout spacing — single source of truth for Layout shell spacing.
 * Mirrors adminLayoutSpec rhythm (mt-12 / mb-12 / mb-8) for dashboard-panel pages.
 */

import { POPUP_FORM_CONTENT_CLASS, POPUP_FORM_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';

/** Margin below Header before page content (matches admin dashboard rhythm). */
export const BUSINESS_MAIN_HEADER_OFFSET_CLASS = 'mt-12';

function joinBusinessLayoutClasses(base, extraClasses = '') {
  const extra = (extraClasses || '').trim();
  return extra ? `${base} ${extra}` : base;
}

/** Combine main header offset with optional extra Tailwind classes. */
export function businessMainHeaderOffsetClass(extraClasses = '') {
  return joinBusinessLayoutClasses(BUSINESS_MAIN_HEADER_OFFSET_CLASS, extraClasses);
}

/** Margin below page title before content (list / settings pages). */
export const BUSINESS_PAGE_TITLE_OFFSET_CLASS = 'mb-12';

/** Combine page title bottom spacing with optional extra Tailwind classes. */
export function businessPageTitleOffsetClass(extraClasses = '') {
  return joinBusinessLayoutClasses(BUSINESS_PAGE_TITLE_OFFSET_CLASS, extraClasses);
}

/** Standard business dashboard page title spacing. */
export const BUSINESS_PAGE_TITLE_CLASS = BUSINESS_PAGE_TITLE_OFFSET_CLASS;

/** Centered page title (post lists, settings forms). */
export const BUSINESS_CENTERED_PAGE_TITLE_CLASS = businessPageTitleOffsetClass('text-center pb-0');

/** Left-aligned page title (tickets, detail headers). */
export const BUSINESS_LIST_PAGE_TITLE_CLASS = businessPageTitleOffsetClass('text-offblack pb-0');

/** Margin below search + filter toolbar before table/content. */
export const BUSINESS_TOOLBAR_OFFSET_CLASS = 'mb-8';

export function businessToolbarOffsetClass(extraClasses = '') {
  return joinBusinessLayoutClasses(BUSINESS_TOOLBAR_OFFSET_CLASS, extraClasses);
}

/** Standard list page search + filters row. */
export const BUSINESS_TOOLBAR_CLASS = businessToolbarOffsetClass('px-4');

/** Margin below list table before pagination/footer. */
export const BUSINESS_TABLE_OFFSET_CLASS = 'mb-12';

export function businessTableOffsetClass(extraClasses = '') {
  return joinBusinessLayoutClasses(BUSINESS_TABLE_OFFSET_CLASS, extraClasses);
}

/** Standard list page table container. */
export const BUSINESS_TABLE_CLASS = businessTableOffsetClass('!bg-white lg:px-4 w-full');

/** Margin below form action row (Save / Draft) at page bottom. */
export const BUSINESS_FORM_FOOTER_OFFSET_CLASS = 'mb-12';

export function businessFormFooterOffsetClass(extraClasses = '') {
  return joinBusinessLayoutClasses(BUSINESS_FORM_FOOTER_OFFSET_CLASS, extraClasses);
}

/** Standard form page bottom action row. */
export const BUSINESS_FORM_FOOTER_CLASS = businessFormFooterOffsetClass('mt-12 px-4');

/** Step / inline form action row — top margin only (Next, Save, Back). */
export const BUSINESS_FORM_ACTION_ROW_CLASS = 'mt-12';

export const BUSINESS_FORM_ACTION_ROW_END_CLASS =
  'flex items-center justify-end mt-12 w-full';

export const BUSINESS_FORM_ACTION_ROW_BETWEEN_CLASS =
  'flex items-center justify-between mt-12 w-full';

/** Gap between stacked form sections. */
export const BUSINESS_FORM_SECTION_GAP_CLASS = 'mt-12';

/** Margin below a page section (KPI row, chart block, form block). */
export const BUSINESS_SECTION_OFFSET_CLASS = 'mb-12';

export function businessSectionOffsetClass(extraClasses = '') {
  return joinBusinessLayoutClasses(BUSINESS_SECTION_OFFSET_CLASS, extraClasses);
}

/** Section block bottom spacing. */
export const BUSINESS_SECTION_CLASS = businessSectionOffsetClass();

/** Form panel on settings pages. */
export const BUSINESS_FORM_PANEL_CLASS = '!bg-white lg:px-4 w-full';

/** Chart / report / card content panel. */
export const BUSINESS_CONTENT_PANEL_CLASS =
  '!bg-white w-full flex flex-col rounded-xl';

/** Detail page header block before tabs or body. */
export const BUSINESS_DETAIL_MAIN_HEADER_OFFSET_CLASS = 'mb-4';

export function businessDetailMainHeaderOffsetClass(extraClasses = '') {
  return joinBusinessLayoutClasses(BUSINESS_DETAIL_MAIN_HEADER_OFFSET_CLASS, extraClasses);
}

export const BUSINESS_DETAIL_MAIN_HEADER_CLASS =
  businessDetailMainHeaderOffsetClass('!bg-white');

/** Post list card grid. */
export const BUSINESS_POST_GRID_CLASS =
  'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mx-auto px-4';

/** Load-more / list footer action spacing. */
export const BUSINESS_LIST_FOOTER_CLASS = 'flex justify-center mt-12';

/** Inbox / messages list and thread content width. */
export const BUSINESS_INBOX_CONTENT_CLASS = 'mx-auto w-full max-w-[1200px]';

/** Subscription billing — narrow masonry column inside tp-container-small. */
export const BUSINESS_SUBSCRIPTION_CONTENT_CLASS = 'mx-auto w-full max-w-[818px]';

/** Centered wizard shell (create post, edit post). */
export const BUSINESS_WIZARD_SHELL_CLASS =
  'lg:px-5 lg:pb-10 relative !bg-white w-full';

/** Centered wizard viewport — vertical/horizontal centering for form column. */
export const BUSINESS_WIZARD_VIEWPORT_CLASS =
  'overflow-auto relative flex items-center justify-center w-full';

/** Inner rhythm inside wizard column (title + fields). */
export const BUSINESS_WIZARD_INNER_CLASS = 'w-full';

/** Single column shell for centered wizards — same width as popup forms (352px). */
export const BUSINESS_WIZARD_COLUMN_CLASS = POPUP_FORM_CONTENT_CLASS;

/** Standard wizard column width. */
export const BUSINESS_WIZARD_CONTENT_CLASS =
  `${POPUP_FORM_CONTENT_WIDTH_CLASS} mx-auto`;

/** Wider wizard column (review, account forms). */
export const BUSINESS_WIZARD_CONTENT_WIDE_CLASS =
  `${POPUP_FORM_CONTENT_WIDTH_CLASS} mx-auto`;

/** Post type selection row layout. */
export const BUSINESS_WIZARD_SELECT_TYPE_CLASS =
  'flex flex-col lg:flex-row items-center justify-center gap-8 w-full xl:w-[832px] 2xl:w-[832px] mx-auto';

/** Wizard step title on AdminPageTitle. */
export const BUSINESS_WIZARD_TITLE_CLASS =
  businessPageTitleOffsetClass('text-center pb-0 text-offblack');

/**
 * Inbox thread panel height — header + layout offset (was 80px before mt-12 header spacing).
 * @see MessageLayout
 */
export const BUSINESS_INBOX_THREAD_HEIGHT_CLASS = 'h-[calc(100vh-160px)]';

/** Customer inbox thread height (matches business after mt-12 header spacing). */
export const CUSTOMER_INBOX_THREAD_HEIGHT_CLASS = 'h-[calc(100vh-160px)]';

/** Pick thread height class from dashboard pathname (business vs customer). */
export function inboxThreadHeightClass(pathname = '') {
  return pathname.includes('/business-dashboard/')
    ? BUSINESS_INBOX_THREAD_HEIGHT_CLASS
    : CUSTOMER_INBOX_THREAD_HEIGHT_CLASS;
}

/** Chat row — avatar aligned to top of bubble. */
export function businessChatMessageRowClass(sender) {
  return `flex max-w-[48%] ${sender === 'business' ? 'flex-row-reverse' : 'flex-row'} items-start gap-2`;
}

/** Chat bubble — flat corner on top-adjacent side (matches admin MessageDetail). */
export function businessChatBubbleClass(sender) {
  return `px-4 py-2 rounded-xl ${
    sender === 'business'
      ? 'bg-primaryblue text-white rounded-tr-md'
      : 'bg-mistygray text-offblack rounded-tl-md'
  }`;
}

/** Quote / card bubble (incoming, left side). */
export const BUSINESS_CHAT_INCOMING_CARD_CLASS =
  'bg-mistygray text-offblack rounded-xl rounded-tl-md px-4 py-3 max-w-full';

/** Shared avatar size in thread bubbles. */
export const BUSINESS_CHAT_AVATAR_CLASS = 'w-8 h-8 rounded-full object-cover';

/**
 * Wizard / vertically centered flows — layout header offset would push content off-center.
 * Create post, edit post, create ticket, etc.
 */
export function isBusinessCenteredContentPath(pathname = '') {
  return (
    pathname.includes('/business-dashboard/posts/create-post') ||
    pathname.includes('/business-dashboard/posts/edit/') ||
    pathname.includes('/business-dashboard/tickets/create')
  );
}
